import {
    type OrthographicCamera,
    Plane,
    Raycaster,
    Vector2,
    Vector3,
} from 'three';

export type PlaneProjection = {
    /** Shared raycaster, configured by `project()` from each pointer event. */
    readonly raycaster: Raycaster;
    /**
     * Project a pointer event into world coordinates by raycasting from the
     * camera through the pointer position and intersecting against the plane.
     */
    readonly project: (event: PointerEvent) => Vector2;
};

const DEFAULT_PLANE = new Plane(new Vector3(0, 0, 1), 0);

/**
 * Build a reusable pointer-to-world projection helper for a canvas + camera.
 *
 * Intersection is computed against `plane` (default: z = 0). The returned
 * `raycaster` is configured by each `project()` call and may be reused for
 * subsequent `raycaster.intersectObjects()` calls in the same event handler
 * to avoid redundant `setFromCamera` work.
 */
export function createPlaneProjection(
    canvas: HTMLCanvasElement,
    camera: OrthographicCamera,
    plane: Plane = DEFAULT_PLANE,
): PlaneProjection {
    const raycaster = new Raycaster();
    const ndc = new Vector2();
    const intersection = new Vector3();

    return {
        raycaster,
        project: (event: PointerEvent): Vector2 => {
            const rect = canvas.getBoundingClientRect();
            ndc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            ndc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(ndc, camera);
            raycaster.ray.intersectPlane(plane, intersection);
            return new Vector2(intersection.x, intersection.y);
        },
    };
}
